import {
  alpha,
  Box,
  BoxProps,
  Typography,
  TypographyProps,
  useTheme,
} from "@mui/material";
import { clsx } from "clsx";
import { FC, ReactNode } from "react";
import { FieldAdornmentTypeWithProps, HFieldProps } from "../types";
import { HImage } from "./HImage";
import { HintSuffix } from "./HintSuffix";
import { HTextField, HTextFieldProps } from "./HTextField";
import { HTooltipSuffix } from "./HTooltip";
import { HypeIcon } from "./HypeIcon";

export type HLabelFieldProps = HFieldProps & {
  containerProps?: BoxProps;
  className?: string;
  wrapperProps?: BoxProps;
  labelWrapperProps?: BoxProps;
  startAdornmentProps?: BoxProps;
  endAdornmentProps?: BoxProps;
  labelSecondaryProps?: TypographyProps;
  labelSecondaryAdornment?: FieldAdornmentTypeWithProps;
  labelAsInput?: boolean;
  labelInputProps?: Partial<HTextFieldProps>;
  labelSecondaryAdornmentNode?: ReactNode;
  fullWidth?: boolean;
};

export type HLabelAdornmentWithSeparator = FieldAdornmentTypeWithProps & {
  separatorPosition?: "end" | "start";
};

const HLabelAdornment: FC<HLabelAdornmentWithSeparator> = ({
  text,
  icon,
  iconPosition = "left",
  rounded,
  roundedBgColor = "divider",
  iconColor = "secondary.dark",
  textColor = "secondary.dark",
  textWeight = 400,
  imagePosition = "left",
  imageUrl,
  imageWidth = 20,
  imageHeight = 20,
  customAdornmentNode,
  wrapperProps,
  withSeparator,
  separatorPosition = "end",
}) => {
  const showRoundedBox = rounded;
  const { palette } = useTheme();
  const alphaBgColor = alpha(palette.primary.dark, 0.2);
  return (
    <Box
      display="flex"
      className={clsx("label-adornment", wrapperProps?.className)}
      flexDirection="row"
      alignItems="center"
      gap={3}
      {...wrapperProps}
    >
      {withSeparator && separatorPosition === "start" && (
        <Box sx={{ color: textColor }}>|</Box>
      )}

      {imageUrl && imagePosition === "left" && (
        <HImage
          src={imageUrl}
          width={imageWidth}
          height={imageHeight}
          alt="image"
        />
      )}
      {icon && iconPosition === "left" && (
        <Box
          className="icon-left"
          sx={{
            width: showRoundedBox ? 40 : "initial",
            height: showRoundedBox ? 40 : "initial",
            borderRadius: showRoundedBox ? "60px" : "0px",
            backgroundColor: showRoundedBox ? alphaBgColor : "inherit",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: showRoundedBox ? "hidden" : "inherit",
            textAlign: showRoundedBox ? "center" : "inherit",
          }}
        >
          <HypeIcon name={icon} color={iconColor} />
        </Box>
      )}
      {text && (
        <Typography
          variant="body"
          className="label-adornment-text"
          sx={{
            width: showRoundedBox ? 40 : "initial",
            height: showRoundedBox ? 40 : "initial",
            borderRadius: showRoundedBox ? "60px" : "0px",
            backgroundColor: showRoundedBox ? roundedBgColor : "inherit",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: showRoundedBox ? "hidden" : "inherit",
            textAlign: showRoundedBox ? "center" : "inherit",
            color: textColor,
            fontWeight: textWeight,
          }}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      )}
      {icon && iconPosition === "right" && (
        <Box
          className="icon-left"
          sx={{
            width: showRoundedBox ? 40 : "initial",
            height: showRoundedBox ? 40 : "initial",
            borderRadius: showRoundedBox ? "60px" : "0px",
            backgroundColor: showRoundedBox ? alphaBgColor : "inherit",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: showRoundedBox ? "hidden" : "inherit",
            textAlign: showRoundedBox ? "center" : "inherit",
          }}
        >
          <HypeIcon weight={textWeight} name={icon} color={iconColor} />
        </Box>
      )}
      {imageUrl && imagePosition === "right" && (
        <HImage
          src={imageUrl}
          width={imageWidth}
          height={imageHeight}
          alt="image"
        />
      )}
      {customAdornmentNode}
      {withSeparator && separatorPosition === "end" && (
        <Box sx={{ color: textColor }}>|</Box>
      )}
    </Box>
  );
};

const HLabel: FC<HLabelFieldProps> = ({
  text,
  textVariant,
  labelDescription,
  fieldPrefix,
  fieldSuffix,
  hint,
  labelHintPosition = "end",
  labelSuffixPosition = "end",
  labelColorVariant = "light",
  labelColor,
  labelSecondaryColor,
  labelWeightVariant,
  size = "normal",
  labelSecondary,
  endAdornmentProps,
  startAdornmentProps,
  labelWrapperProps,
  containerProps,
  wrapperProps,
  labelSecondaryProps,
  labelSecondaryAdornment,
  labelAsInput,
  labelInputProps,
  labelSecondaryAdornmentNode,
  disabled,
  fullWidth = true,
  name,
  tooltip,
  className,
}) => {
  const { palette } = useTheme();
  let labelFgColor = palette.secondary.dark;
  let labelSecondaryFgColor = palette.secondary.light;

  if (labelColor) {
    labelFgColor = labelColor;
  } else if (labelColorVariant === "light") {
    labelFgColor = palette.secondary.dark;
  } else if (labelColorVariant === "dark") {
    labelFgColor = palette.text.primary;
  }

  if (labelSecondaryColor) {
    labelSecondaryFgColor = labelSecondaryColor;
  } else if (labelColorVariant === "light") {
    labelSecondaryFgColor = palette.secondary.light;
  } else if (labelColorVariant === "dark") {
    labelSecondaryFgColor = palette.text.primary;
  }

  if (disabled) {
    labelSecondaryFgColor = palette.text.disabled;
    labelFgColor = palette.text.disabled;
  }

  const {
    ref: _labelInputRef,
    sx: labelInputSx,
    ...labelInputRest
  } = labelInputProps ?? {};

  return (
    <Box
      display="flex"
      className={`hlabel ${className}`}
      flexDirection="column"
      flexBasis="100%"
      gap={3}
      {...wrapperProps}
    >
      <Box
        display="flex"
        flexDirection="row"
        flexBasis="100%"
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        {...containerProps}
        sx={{
          minHeight: size === "medium" ? 48 : "auto",
          ...containerProps?.sx,
        }}
      >
        {fieldPrefix && (
          <HLabelAdornment
            separatorPosition="start"
            {...fieldPrefix}
            {...startAdornmentProps}
          />
        )}
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          {...labelWrapperProps}
        >
          {hint && labelHintPosition === "inline" && <HintSuffix {...hint} />}
          {text && (
            <Box display="flex" flexDirection="row" alignItems="center">
              <Typography
                className="label--label"
                data-variant={textVariant}
                variant={textVariant ?? "body"}
                sx={{
                  color: labelFgColor,
                  fontWeight: labelWeightVariant,
                  pb: 0,
                  width: fullWidth ? "100%" : "auto",
                }}
                dangerouslySetInnerHTML={{ __html: text }}
              />
              {fieldSuffix && labelSuffixPosition === "inline" && (
                <HLabelAdornment
                  separatorPosition="start"
                  {...fieldSuffix}
                  {...endAdornmentProps}
                />
              )}
            </Box>
          )}
          <Box display="flex" flexDirection="row" gap={3}>
            {labelSecondaryAdornment && (
              <HLabelAdornment
                separatorPosition="end"
                {...labelSecondaryAdornment}
              />
            )}
            {labelSecondaryAdornmentNode}
            {labelAsInput ? (
              <HTextField
                name={name}
                value={labelSecondary ?? ""}
                disabled={disabled}
                {...labelInputRest}
                multiline
                spellCheck={false}
                sx={{
                  caretColor: "transparent",
                  ".MuiInputBase-root": {
                    p: 0,
                    minHeight: "auto",
                  },
                  ...labelInputSx,
                }}
              />
            ) : (
              <Typography
                variant="body"
                className="label-secondary"
                dangerouslySetInnerHTML={{ __html: labelSecondary ?? "" }}
                {...labelSecondaryProps}
                sx={{
                  color: labelSecondaryFgColor,
                  ...labelSecondaryProps?.sx,
                }}
              />
            )}
          </Box>
        </Box>
        {fieldSuffix && labelSuffixPosition === "end" && (
          <HLabelAdornment
            separatorPosition="start"
            {...fieldSuffix}
            {...endAdornmentProps}
          />
        )}
        {hint && labelHintPosition === "end" && <HintSuffix {...hint} />}
        {tooltip && <HTooltipSuffix {...tooltip} />}
      </Box>
      {labelDescription && (
        <Box
          display="flex"
          className="label-description-container"
          flexDirection="row"
          flexBasis="100%"
          justifyContent="space-between"
          alignItems="center"
          p={5}
          gap={3}
          sx={{
            backgroundColor: "background.default",
            fontSize: 14,
            borderRadius: "8px",
            color: "secondary.dark",
          }}
        >
          <Typography
            variant="body"
            className="label-description"
            dangerouslySetInnerHTML={{ __html: labelDescription }}
          />
        </Box>
      )}
    </Box>
  );
};

export { HLabel, HLabelAdornment };
